'use client'

import React from 'react'
import { useTranslation } from 'react-i18next'
import { Chip, Chips, IconButton, Input, SettingRow } from '@kiao-voice/ui-kit'
import { Plus } from 'lucide-react'
import {
  AUDIO_BATCH_MAX_ENHANCEMENT_HINTS,
  AUDIO_BATCH_MAX_ENHANCEMENT_HINT_CHARS,
} from '@kiao-voice/api-contract/batch-transcription'
import type { HintTerms } from './use-hint-terms'
import type { UploadDictionaries } from './use-upload-dictionaries'

export interface UploadHintsSectionProps {
  /** The typed-term half of the vocabulary, from `use-hint-terms`. */
  hintTerms: HintTerms
  /** The dictionary half, from `use-upload-dictionaries`. */
  dictionary: Pick<
    UploadDictionaries,
    'dictionaries' | 'entryStates' | 'toggleDictionary' | 'loadDictionary'
  >
  submitting: boolean
  openSaveDialog: (term: string) => void
  saveNotice: string | null
}

/**
 * The vocabulary row: the term the user is typing, every term this upload will
 * ship (typed and from each checked dictionary, each group able to fail and
 * retry on its own), and the suggestions offered from past uploads.
 */
export function UploadHintsSection({
  hintTerms,
  dictionary,
  submitting,
  openSaveDialog,
  saveNotice,
}: UploadHintsSectionProps) {
  const { t } = useTranslation('web')
  const {
    hintInput,
    setHintInput,
    hintLimitReached,
    overHintLimit,
    expandedHints,
    addHint,
    addHintTerm,
    removeHint,
    excludeHint,
    restoreHint,
    suggestions,
    suggestionsCollapsed,
    toggleSuggestionsCollapsed,
  } = hintTerms
  const {
    dictionaries,
    entryStates: dictionaryEntryStates,
    toggleDictionary,
    loadDictionary,
  } = dictionary
  return (
    <SettingRow
      label={t('batchTranscription.hintsTitle')}
      control={
        <div className="p-input w-full items-center md:w-[264px]">
          <Input
            type="text"
            aria-label={t('batchTranscription.hintsInputLabel')}
            value={hintInput}
            disabled={submitting || hintLimitReached}
            maxLength={AUDIO_BATCH_MAX_ENHANCEMENT_HINT_CHARS + 1}
            placeholder={
              hintLimitReached
                ? t('batchTranscription.hintsInputFullPlaceholder', {
                    max: AUDIO_BATCH_MAX_ENHANCEMENT_HINTS,
                  })
                : t('batchTranscription.hintsInputPlaceholder')
            }
            onChange={(event) => setHintInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                addHint()
              }
            }}
          />
          <IconButton
            variant="ghost"
            aria-label={t('batchTranscription.hintsAddLabel')}
            title={t('batchTranscription.hintsAddLabel')}
            disabled={submitting || hintLimitReached}
            onClick={addHint}
          >
            <Plus aria-hidden="true" className="size-4" strokeWidth={2} />
          </IconButton>
        </div>
      }
      supportingContent={
        <>
          {suggestions.length > 0 ? (
            <div
              className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1"
              data-testid="hint-suggestions"
            >
              <span className="text-xs text-[var(--muted)]">
                {t('batchTranscription.suggestionsLabel')}
              </span>
              {suggestionsCollapsed
                ? null
                : suggestions.map((suggestion) => (
                    <span
                      key={suggestion.term}
                      className="inline-flex items-center gap-1.5 text-xs"
                    >
                      <button
                        type="button"
                        className="text-[var(--fg)] underline underline-offset-4"
                        aria-label={t('batchTranscription.suggestionsAddLabel', {
                          term: suggestion.term,
                        })}
                        disabled={submitting}
                        onClick={() => addHintTerm(suggestion.term)}
                      >
                        {suggestion.term}
                      </button>
                      {suggestion.eligibleForDictionary ? (
                        <button
                          type="button"
                          className="text-[var(--muted)] underline underline-offset-4"
                          aria-label={t('batchTranscription.suggestionsIncubateLabel', {
                            term: suggestion.term,
                          })}
                          disabled={submitting}
                          onClick={() => openSaveDialog(suggestion.term)}
                        >
                          {t('batchTranscription.suggestionsIncubateText')}
                        </button>
                      ) : null}
                    </span>
                  ))}
              <button
                type="button"
                className="text-xs text-[var(--muted)] underline underline-offset-4"
                onClick={toggleSuggestionsCollapsed}
              >
                {suggestionsCollapsed
                  ? t('batchTranscription.suggestionsExpand')
                  : t('batchTranscription.suggestionsCollapse')}
              </button>
            </div>
          ) : null}
          <p className="mt-2 text-xs leading-5 text-[var(--muted)]">
            {t('transcriptions.upload.hintsDescription')}
            {expandedHints.totalCount > 0 ? (
              <span className="meta num">
                {' '}
                {t('batchTranscription.hintsCount', {
                  count: expandedHints.totalCount,
                  max: AUDIO_BATCH_MAX_ENHANCEMENT_HINTS,
                })}
              </span>
            ) : null}
          </p>
          {overHintLimit ? (
            <p className="mt-2 text-xs leading-5 text-[var(--error)]">
              {t('batchTranscription.hintsOverLimit', {
                total: expandedHints.totalCount,
                max: AUDIO_BATCH_MAX_ENHANCEMENT_HINTS,
              })}
            </p>
          ) : null}
          {expandedHints.dictionaryGroups.map((group) => {
            const dictionary = dictionaries.find((item) => item.id === group.dictionaryId)
            const entryState = dictionaryEntryStates[group.dictionaryId]
            const pending = entryState?.status === 'loading' || entryState === undefined
            const failed = entryState?.status === 'failed'
            return (
              <div key={group.dictionaryId} className="mt-3">
                <div className="flex items-baseline gap-2">
                  <p className="text-xs font-medium leading-5">{group.dictionaryName}</p>
                  <button
                    type="button"
                    className="ml-auto text-xs text-[var(--muted)] underline underline-offset-4 hover:text-[var(--fg)]"
                    aria-label={t('batchTranscription.dictionaryDoNotUseLabel', {
                      name: group.dictionaryName,
                    })}
                    disabled={submitting}
                    onClick={() => dictionary && void toggleDictionary(dictionary, false)}
                  >
                    {t('batchTranscription.dictionaryDoNotUse')}
                  </button>
                </div>
                {pending ? (
                  <Chips
                    className="mt-1"
                    data-testid={`dictionary-hint-loading-${group.dictionaryId}`}
                  >
                    {Array.from(
                      { length: Math.min(dictionary?.entryCount ?? 1, 3) },
                      (_, index) => (
                        <span
                          key={index}
                          aria-hidden="true"
                          className="h-7 w-16 animate-pulse rounded-full border border-border bg-[var(--surface)]"
                        />
                      )
                    )}
                  </Chips>
                ) : failed ? (
                  <p
                    className="mt-1 text-xs leading-5 text-[var(--error)]"
                    data-testid={`dictionary-hint-error-${group.dictionaryId}`}
                  >
                    {t('batchTranscription.dictionaryEntriesFailed', {
                      name: group.dictionaryName,
                    })}{' '}
                    <button
                      type="button"
                      className="underline underline-offset-4"
                      aria-label={t('batchTranscription.dictionaryRetryLabel', {
                        name: group.dictionaryName,
                      })}
                      onClick={() => dictionary && void loadDictionary(dictionary)}
                    >
                      {t('batchTranscription.dictionaryRetry')}
                    </button>
                  </p>
                ) : (
                  <>
                    {group.skippedCount > 0 ? (
                      <p className="mt-1 text-xs leading-5 text-[var(--muted)]">
                        {t('batchTranscription.dictionarySkipped', {
                          count: group.skippedCount,
                        })}
                      </p>
                    ) : null}
                    <Chips
                      className="mt-1"
                      data-testid={`dictionary-hint-tags-${group.dictionaryId}`}
                    >
                      {group.hints.map((term) => (
                        <Chip
                          key={term}
                          onRemove={() => excludeHint(term)}
                          removeLabel={t('batchTranscription.hintsRemoveLabel', { term })}
                        >
                          {term}
                        </Chip>
                      ))}
                      {group.excluded.map((term) => (
                        <button
                          key={term}
                          type="button"
                          className="inline-flex min-h-7 items-center rounded-full border border-dashed border-border bg-[var(--surface)] px-3 text-sm text-[var(--muted)]"
                          aria-label={t('batchTranscription.hintsRestoreLabel', { term })}
                          onClick={() => restoreHint(term)}
                        >
                          {term}
                        </button>
                      ))}
                    </Chips>
                  </>
                )}
              </div>
            )
          })}
          {expandedHints.manual.hints.length > 0 || expandedHints.manual.excluded.length > 0 ? (
            <div className="mt-3">
              <p className="text-xs leading-5 text-[var(--muted)]">
                {t('batchTranscription.hintsManualGroupLabel')}
              </p>
              <Chips className="mt-1" data-testid="enhancement-hint-tags">
                {expandedHints.manual.hints.map((term) => (
                  <span key={term} className="inline-flex items-center gap-0.5">
                    <Chip
                      onRemove={() => removeHint(term)}
                      removeLabel={t('batchTranscription.hintsRemoveLabel', { term })}
                    >
                      {term}
                    </Chip>
                    <button
                      type="button"
                      className="text-xs text-[var(--muted)] underline underline-offset-4 hover:text-[var(--fg)]"
                      disabled={submitting}
                      aria-label={t('batchTranscription.saveToDictionaryLabel', { term })}
                      onClick={() => openSaveDialog(term)}
                    >
                      ＋ {t('batchTranscription.saveToDictionaryText')}
                    </button>
                  </span>
                ))}
                {expandedHints.manual.excluded.map((term) => (
                  <button
                    key={term}
                    type="button"
                    className="inline-flex min-h-7 items-center rounded-full border border-dashed border-border bg-[var(--surface)] px-3 text-sm text-[var(--muted)]"
                    aria-label={t('batchTranscription.hintsRestoreLabel', { term })}
                    onClick={() => restoreHint(term)}
                  >
                    {term}
                  </button>
                ))}
              </Chips>
            </div>
          ) : null}
          {saveNotice ? (
            <p className="mt-2 text-xs leading-5 text-[var(--muted)]">{saveNotice}</p>
          ) : null}
        </>
      }
    />
  )
}
